import { z } from 'zod';
import { DecimalStringSchema, IsoDateSchema } from './primitives.js';

export const DashboardPositionSchema = z.object({
  assetId: z.number().int().positive(),
  chainId: z.number().int().positive(),
  symbol: z.string(),
  name: z.string(),
  qty: DecimalStringSchema,
  cumpEur: DecimalStringSchema,
  spotEur: DecimalStringSchema,
  priceObservedAt: z.number().int().nonnegative().nullable().optional(),
  valueEur: DecimalStringSchema,
  unrealizedPlEur: DecimalStringSchema,
  allocationPct: DecimalStringSchema,
  /** False when the asset is held but couldn't be valued in EUR (no price, or
   *  an unreconstructable CUMP ledger). qty is still meaningful; the EUR columns
   *  are 0 and should be shown as unavailable. */
  valued: z.boolean(),
});

export const DashboardValueOverTimePointSchema = z.object({
  date: IsoDateSchema,
  bySymbol: z.record(z.string(), DecimalStringSchema),
});

export const DashboardPlOverTimePointSchema = z.object({
  date: IsoDateSchema,
  realizedPlEur: DecimalStringSchema,
  unrealizedPlEur: DecimalStringSchema,
  totalPlEur: DecimalStringSchema,
});

export const DashboardAlertSchema = z.object({
  id: z.string(),
  severity: z.enum(['info', 'warn', 'error']),
  label: z.string(),
  detail: z.string().optional(),
  actionHref: z.string().optional(),
});

export const DashboardSummarySchema = z.object({
  asOfDate: IsoDateSchema,
  readiness: z.enum(['ready', 'action_required']),
  totalValueEur: DecimalStringSchema,
  unrealizedPlEur: DecimalStringSchema,
  realizedPlYtdEur: DecimalStringSchema,
  realizedPlFiscalEur: DecimalStringSchema,
  /** Covering fiscal period, or null when fiscal P&L falls back to calendar YTD. */
  fiscalPeriodLabel: z.string().nullable(),
  heldAssetsCount: z.number().int().nonnegative(),
  positions: z.array(DashboardPositionSchema),
  valueOverTime: z.array(DashboardValueOverTimePointSchema),
  plOverTime: z.array(DashboardPlOverTimePointSchema),
  alerts: z.array(DashboardAlertSchema),
});

export type DashboardSummary = z.infer<typeof DashboardSummarySchema>;
export type DashboardPosition = z.infer<typeof DashboardPositionSchema>;
export type DashboardValueOverTimePoint = z.infer<typeof DashboardValueOverTimePointSchema>;
export type DashboardPlOverTimePoint = z.infer<typeof DashboardPlOverTimePointSchema>;
export type DashboardAlertDto = z.infer<typeof DashboardAlertSchema>;
